import React, { useState, useEffect } from 'react';
import { Book, HelpCircle, FileText, Shield, ScrollText, Bell, MessageSquare, AlertTriangle, Home } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const HelpCenter = () => {
  const [activeDoc, setActiveDoc] = useState('getting-started');
  const [docContent, setDocContent] = useState('');

  // Document metadata
  const documents = {
    'getting-started': {
      title: 'Getting Started',
      icon: <Home className="h-5 w-5" />,
      file: 'BETA_GETTING_STARTED.md',
      description: 'Quick start guide for new users'
    },
    'user-guide': {
      title: 'User Guide',
      icon: <Book className="h-5 w-5" />,
      file: 'USER_GUIDE.md',
      description: 'Complete documentation'
    },
    'faq': {
      title: 'FAQ',
      icon: <HelpCircle className="h-5 w-5" />,
      file: 'FAQ_BETA.md',
      description: 'Frequently asked questions'
    },
    'privacy': {
      title: 'Privacy Policy',
      icon: <Shield className="h-5 w-5" />,
      file: 'PRIVACY_POLICY.md',
      description: 'How we handle your data'
    },
    'terms': {
      title: 'Terms of Service',
      icon: <FileText className="h-5 w-5" />,
      file: 'TERMS_OF_SERVICE.md',
      description: 'Beta program terms'
    },
    'release-notes': {
      title: 'Release Notes',
      icon: <Bell className="h-5 w-5" />,
      file: 'RELEASE_NOTES_RC15.md',
      description: 'What\'s new in RC1.5'
    }
  };

  // Load markdown content (simplified - in production, use markdown parser)
  useEffect(() => {
    const loadDocument = async () => {
      try {
        const response = await fetch(`/docs/${documents[activeDoc].file}`);
        const text = await response.text();
        setDocContent(text);
      } catch (error) {
        console.error('Failed to load document:', error);
        setDocContent('# Error Loading Document\n\nPlease try again later.');
      }
    };
    
    loadDocument();
  }, [activeDoc]);

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Help Center</h1>
        <p className="text-muted-foreground mt-2">
          Documentation, guides, and support resources
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/feedback-manager'}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Submit Feedback
            </CardTitle>
            <CardDescription>
              Share your thoughts, report bugs, or request features
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              Open Feedback Center
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/beta-incidents'}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Report Issue
            </CardTitle>
            <CardDescription>
              Report critical bugs or incidents
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              View Incidents
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveDoc('faq')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-green-600" />
              Quick Answers
            </CardTitle>
            <CardDescription>
              Find answers to common questions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              View FAQ
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Documentation Browser */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Documentation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(documents).map(([key, doc]) => (
                <Button
                  key={key}
                  variant={activeDoc === key ? 'default' : 'ghost'}
                  className="w-full justify-start"
                  onClick={() => setActiveDoc(key)}
                >
                  {doc.icon}
                  <span className="ml-2">{doc.title}</span>
                </Button>
              ))}
            </CardContent>
          </Card>

          {/* Keyboard Shortcuts Card */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-sm">Keyboard Shortcuts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Feedback</span>
                <kbd className="px-2 py-1 bg-muted rounded">Ctrl+Shift+F</kbd>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Document Content */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {documents[activeDoc].icon}
                {documents[activeDoc].title}
              </CardTitle>
              <CardDescription>
                {documents[activeDoc].description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Simple markdown display - in production, use a proper markdown renderer like react-markdown */}
              <div className="prose prose-sm max-w-none dark:prose-invert">
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                  {docContent}
                </pre>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Additional Resources */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Resources</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <h3 className="font-semibold mb-2">🎯 Beta Dashboard</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Monitor Beta program status and metrics
              </p>
              <Button variant="outline" size="sm" onClick={() => window.location.href = '/beta-dashboard'}>
                View Dashboard
              </Button>
            </div>

            <div className="p-4 border rounded-lg">
              <h3 className="font-semibold mb-2">📊 Analytics</h3>
              <p className="text-sm text-muted-foreground mb-3">
                View usage analytics and insights
              </p>
              <Button variant="outline" size="sm" onClick={() => window.location.href = '/beta-analytics'}>
                View Analytics
              </Button>
            </div>

            <div className="p-4 border rounded-lg">
              <h3 className="font-semibold mb-2">💬 Feedback Manager</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Review and manage user feedback
              </p>
              <Button variant="outline" size="sm" onClick={() => window.location.href = '/feedback-manager'}>
                Manage Feedback
              </Button>
            </div>

            <div className="p-4 border rounded-lg">
              <h3 className="font-semibold mb-2">🐛 Incident Manager</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Track and resolve Beta incidents
              </p>
              <Button variant="outline" size="sm" onClick={() => window.location.href = '/beta-incidents'}>
                Manage Incidents
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Support */}
      <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
        <CardHeader>
          <CardTitle>Need More Help?</CardTitle>
          <CardDescription>
            Can't find what you're looking for? We're here to help!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <Button variant="default" className="flex-1" onClick={() => {
              // Trigger feedback modal (Ctrl+Shift+F)
              const event = new KeyboardEvent('keydown', {
                key: 'F',
                ctrlKey: true,
                shiftKey: true,
                bubbles: true
              });
              window.dispatchEvent(event);
            }}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Contact Support
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => setActiveDoc('faq')}>
              <HelpCircle className="h-4 w-4 mr-2" />
              Check FAQ
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpCenter;
